import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { ChevronRight, ExternalLink, FileText } from "@/components/icons"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

interface ToolResultViewProps {
  content: string
}

interface ParsedDocument {
  id: string
  filename?: string
  library?: string
  fileUrl?: string
  folderUrl?: string
  modifiedOn?: string
  createdBy?: string
  body: string
}

// Extract a single <tag>...</tag> from a document blob. Uses non-greedy match
// plus `s` flag so we span newlines. Returns undefined if the tag is missing.
function pickTag(src: string, tag: string): string | undefined {
  const re = new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`)
  const m = src.match(re)
  return m ? m[1].trim() : undefined
}

function parseDocument(doc: string): ParsedDocument | null {
  const idMatch = doc.match(/<document\s+id="([^"]+)"\s*>/)
  if (!idMatch) return null
  const body = pickTag(doc, "content") ?? ""
  return {
    id: idMatch[1],
    filename: pickTag(doc, "filename"),
    library: pickTag(doc, "library"),
    fileUrl: pickTag(doc, "file_url"),
    folderUrl: pickTag(doc, "folder_url"),
    modifiedOn: pickTag(doc, "modified_on"),
    createdBy: pickTag(doc, "created_by"),
    body,
  }
}

// The backend sometimes wraps the tool-call result as a JSON string whose
// `content` field is either (a) a single giant string, or (b) an array of
// <document>...</document> XML-ish snippets. Try hard to recover structure.
interface DecodedResult {
  query?: string
  success?: boolean
  message?: string
  documents: ParsedDocument[]
  /** Anything we couldn't structure — shown as raw JSON fallback. */
  leftover?: unknown
}

function decodeToolResult(raw: string): DecodedResult | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== "object") return null
  const obj = parsed as Record<string, unknown>
  const query = typeof obj.query === "string" ? obj.query : undefined
  const success = typeof obj.success === "boolean" ? obj.success : undefined
  const message = typeof obj.message === "string" ? obj.message : undefined

  const documents: ParsedDocument[] = []
  const content = obj.content
  if (typeof content === "string") {
    // Split on </document> boundaries to recover multiple docs from one string.
    const chunks = content.split(/(?<=<\/document>)/g).filter((s) => s.trim().length > 0)
    for (const chunk of chunks) {
      const d = parseDocument(chunk)
      if (d) documents.push(d)
    }
  } else if (Array.isArray(content)) {
    for (const item of content) {
      if (typeof item !== "string") continue
      const d = parseDocument(item)
      if (d) documents.push(d)
    }
  }

  // If we got nothing useful, bail to raw JSON.
  if (documents.length === 0 && query === undefined && message === undefined) {
    return null
  }

  return { query, success, message, documents, leftover: obj }
}

function DocumentCard({ doc }: { doc: ParsedDocument }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const label = doc.filename ?? doc.id
  const preview = doc.body
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/\s+/g, " ")
    .trim()
  const shortPreview = preview.length > 220 ? preview.slice(0, 220) + "…" : preview

  return (
    <li
      // Skip layout/paint for off-screen cards during resize. Estimated
      // intrinsic height assumes the collapsed 2-line preview; expanded
      // cards will exceed this, but the browser will lay those out fully.
      className="border-border bg-background/40 animate-in fade-in-0 slide-in-from-bottom-1 rounded-md border duration-300 motion-reduce:animate-none"
      style={{ contentVisibility: "auto", containIntrinsicSize: "0 72px" }}
    >
      <button
        aria-expanded={expanded}
        className="hover:bg-muted/40 flex w-full items-start gap-2 px-3 py-2 text-left transition-colors"
        type="button"
        onClick={() => setExpanded((v) => !v)}
      >
        <ChevronRight
          className={cn(
            "text-muted-foreground mt-0.5 size-3.5 shrink-0 transition-transform",
            expanded && "rotate-90"
          )}
        />
        <FileText className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-foreground truncate text-sm font-medium">{label}</span>
            {doc.library && (
              <span className="bg-muted text-muted-foreground shrink-0 rounded px-1.5 py-0.5 text-[10px] tracking-wide uppercase">
                {doc.library}
              </span>
            )}
          </div>
          {!expanded && preview && (
            <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">{shortPreview}</p>
          )}
        </div>
        {doc.fileUrl && (
          <a
            className="text-muted-foreground hover:text-foreground shrink-0"
            href={doc.fileUrl}
            rel="noreferrer"
            target="_blank"
            title={t("deepExplorer.open_source")}
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="size-3.5" />
          </a>
        )}
      </button>
      {expanded && doc.body && (
        <div className="border-border border-t px-3 py-2">
          <pre className="text-foreground/90 text-xs leading-relaxed break-words whitespace-pre-wrap">
            {doc.body}
          </pre>
        </div>
      )}
    </li>
  )
}

export function ToolResultView({ content }: ToolResultViewProps) {
  const { t } = useTranslation()
  const decoded = useMemo(() => decodeToolResult(content), [content])

  // Fallback 1: valid JSON but no recognizable structure → pretty-print.
  const prettyJson = useMemo(() => {
    if (decoded) return null
    try {
      return JSON.stringify(JSON.parse(content), null, 2)
    } catch {
      return null
    }
  }, [content, decoded])

  if (!decoded) {
    return (
      <pre className="overflow-auto p-4 text-xs leading-relaxed">
        <code className="text-foreground font-mono break-words whitespace-pre-wrap">
          {prettyJson ?? content}
        </code>
      </pre>
    )
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      {(decoded.query || decoded.success !== undefined) && (
        <div className="flex items-center gap-2 text-xs">
          {decoded.success !== undefined && (
            <span
              className={cn(
                "rounded px-1.5 py-0.5 font-medium tracking-wide uppercase",
                decoded.success ? STATUS_TONES.success.soft : "bg-destructive/10 text-destructive"
              )}
            >
              {decoded.success ? t("deepExplorer.status_ok") : t("deepExplorer.status_failed")}
            </span>
          )}
          {decoded.query && (
            <span className="text-muted-foreground">
              {t("deepExplorer.query_label")}{" "}
              <span className="text-foreground">&ldquo;{decoded.query}&rdquo;</span>
            </span>
          )}
          {decoded.documents.length > 0 && (
            <span className="text-muted-foreground ml-auto tabular-nums">
              {t("deepExplorer.hits_count", { count: decoded.documents.length })}
            </span>
          )}
        </div>
      )}
      {decoded.message && <p className="text-muted-foreground text-sm">{decoded.message}</p>}
      {decoded.documents.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {decoded.documents.map((d, i) => (
            <DocumentCard key={`${d.id}-${i}`} doc={d} />
          ))}
        </ul>
      )}
    </div>
  )
}
